import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";

import { Button, Checkbox, Col, Input, Modal, Row, Select, Space, Spin, Table, Tag, Typography } from "antd";
import type { TableProps } from "antd";
import { useShallow } from "zustand/react/shallow";
import { settingJobStore } from "src/stores/setting-job-store";
import {
  filteredStatus,
  InformationJob,
  JobFromModule,
  LISTS_RELATE_FUGITIVE_JOB,
  LISTS_RELATE_PRODUCTION_JOB,
  LISTS_RELATE_TAG_PI_JOB,
  LISTS_TAGPI_COMPANY_JOB,
  QueryDuplicateJob,
  QueryTagPIJob,
  ResponseSettingJob,
  SettingJobStatusEnum,
} from "src/entity/settings/job";
import moment from "moment";

import VerticalCenter from "src/components/WrapCenter/VerticalCenter";
import AnimationRender from "./AnimationRender";
import { generateYearArray } from "src/utils/array";
import { MonthFullNameMap, MonthMap, MonthMapMoment, MONTHS_FULL_LISTS_PASCAL, MONTHS_LISTS } from "src/entity/common/time.constant";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import { capitalizeFirstLetter } from "src/utils/manage-string";
import { MdOutlineError, MdInfo } from "react-icons/md";
import { profileStore } from "src/stores/profile-store";

// For check month or year submit report
import { getOptionMonthSubmit as getSubmitCombustion } from "src/services/combustion.service";
import { getOptionMonthSubmit as getSubmitFugitive } from "src/services/fugitive.service";
import { getOptionMonthSubmit as getSubmitFlare } from "src/services/flare.service";

const { Text } = Typography;
const CheckboxGroup = Checkbox.Group;

const getTagColor = (status: SettingJobStatusEnum) => {
  switch (status) {
    case SettingJobStatusEnum.SUCCESS:
      return "success";
    case SettingJobStatusEnum.RUNNING:
      return "processing";
    case SettingJobStatusEnum.FAIL:
      return "error";
  }
};

interface RunngingJobModalProps extends InformationJob {
  fromModule?: string;
}

const RunngingJobModal: React.FC<RunngingJobModalProps> = (informationJob) => {
  const profile = profileStore(useShallow((state) => state.profile));
  const companys = profile?.company;
  const { companyId, selectedMonths, yearFrom, yearTo, year } = informationJob;

  const fromModule = informationJob.fromModule || "";
  const companyRun = companyId === "all" ? "All" : companys?.find((company) => company._id === companyId)?.companyName || "All";
  const yearRun = LISTS_RELATE_FUGITIVE_JOB.includes(fromModule as JobFromModule)
    ? year
    : selectedMonths?.length
      ? moment(selectedMonths[0]).year()
      : moment().year();

  const monthRun = selectedMonths && selectedMonths.length > 0 ? selectedMonths.map((month) => moment(month).format("MMMM")) : [];

  // For Duplicate Data Job
  const yearFromRun = yearFrom;
  const yearToRun = yearTo;

  return (
    <div className="flex flex-col items-center gap-4 mt-4">
      {/* Company */}
      <div className="flex gap-2 items-center">
        <div className="w-[80px] text-right">
          <Text>Company</Text>
        </div>
        <Input className="w-[200px]" readOnly value={companyRun} />
      </div>

      {/* Duplicate Job */}
      {fromModule === JobFromModule.VOC_DUPLICATE_DATA && (
        <>
          <Row className="justify-center items-center gap-x-6">
            <div className="flex items-center gap-x-2">
              <Text>From</Text>
              <Input className="w-[100px]" readOnly value={yearFromRun} />

              <Text>To</Text>
              <Input className="w-[100px]" readOnly value={yearToRun} />
            </div>
          </Row>
        </>
      )}

      {/* Year */}
      {![JobFromModule.VOC_DUPLICATE_DATA].includes(fromModule as JobFromModule) && (
        <div className="flex gap-2 items-center">
          <div className="w-[80px] text-right">
            <Text>Year</Text>
          </div>
          <Input className="w-[200px]" readOnly value={yearRun} />
        </div>
      )}

      {/* Re-Cal Flare or Combustion */}
      {[JobFromModule.COMBUSTION_CALCULATE, JobFromModule.FLARE_CALCULATE, JobFromModule.PRODUCTION_IMPORT_DATA].includes(
        fromModule as JobFromModule,
      ) && (
        <>
          {/* Month */}
          <div className="flex gap-2 items-center">
            <div className="w-[80px] text-right">
              <Text>Month</Text>
            </div>
            <Input.TextArea className="w-[200px]" readOnly value={monthRun?.join(", ")} />
          </div>
        </>
      )}
    </div>
  );
};

const getColumns = (pickFunction: (rec: ResponseSettingJob) => void) => {
  const columns: TableProps<ResponseSettingJob>["columns"] = [
    {
      title: "No",
      key: "no",
      render: (_, __, index) => <Text>{+index + 1}</Text>,
    },
    {
      title: "Job Name",
      dataIndex: "taskName",
      key: "taskName",
      width: "17rem",
      sorter: (a, b) => a.taskName.localeCompare(b.taskName),
      render: (_, record) => <Text>{record.taskName}</Text>,
    },
    {
      title: "Detail",
      dataIndex: "detail",
      key: "detail",
      width: "20rem",
      render: (_, record) => <Text>{record.detail}</Text>,
    },
    {
      title: "Schedule",
      dataIndex: "schedule",
      key: "schedule",
      width: "15rem",
      render: (_, record) => <Text>{record.schedule}</Text>,
    },
    {
      title: "Last Execution",
      key: "day",
      width: "12rem",
      render: (_, record) => <Text>{moment(record.jobStartTime).format("YYYY-MM-DD HH:mm")}</Text>,
    },
    // {
    //   title: "Day",
    //   key: "day",
    //   width: "8rem",
    //   render: (_, record) => <Text>{moment(record.jobStartTime).format("YYYY-MM-DD")}</Text>,
    // },
    // {
    //   title: "Time",
    //   key: "time",
    //   width: "5rem",
    //   render: (_, record) => <Text>{moment(record.jobStartTime).format("HH:mm")}</Text>,
    // },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      filters: filteredStatus,
      onFilter: (value, record) => record.status.startsWith(value as string),
      render: (_, record) => (
        <Row justify={"space-between"}>
          <Tag color={getTagColor(record.status)} className="rounded-[10px]">
            <Row justify={"space-between"} className="w-[120px]">
              <Col span={7}>
                <VerticalCenter>
                  <AnimationRender status={record.status} />
                </VerticalCenter>
              </Col>
              <Col className="text-center" span={record.status === SettingJobStatusEnum.SUCCESS ? 16 : 12}>
                <VerticalCenter>{record.status}</VerticalCenter>
              </Col>

              {record.status === SettingJobStatusEnum.RUNNING && (
                <Col
                  className="flex justify-center items-center cursor-pointer"
                  span={5}
                  onClick={() =>
                    Modal.info({
                      title: <ModalTitle title={record.taskName} position={"center"} />,
                      icon: null,
                      content: <RunngingJobModal {...(record.informationJob || {})} fromModule={record.fromModule} />,
                      closable: true,
                      footer: null,
                      maskClosable: true,
                    })
                  }
                >
                  <MdInfo size={24} color={"#1890ff"} />
                </Col>
              )}

              {record.status === SettingJobStatusEnum.FAIL && record.error && (
                <Col
                  className="flex justify-center items-center cursor-pointer"
                  span={5}
                  onClick={() =>
                    Modal.error({
                      title: record.taskName,
                      content: record.error,
                      closable: true,
                      footer: null,
                      maskClosable: true,
                    })
                  }
                >
                  <MdOutlineError size={24} color="red" />
                </Col>
              )}
            </Row>
          </Tag>
        </Row>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const wordingDisplay = record?.fromModule === JobFromModule.VOC_DUPLICATE_DATA ? "Duplicate" : "Re-Run";
        return (
          <Space size="middle">
            <Button type="primary" onClick={() => pickFunction(record)} disabled={record.status === SettingJobStatusEnum.RUNNING}>
              {wordingDisplay}
            </Button>
          </Space>
        );
      },
    },
  ];

  return columns;
};

const SettingJobTable = () => {
  const profile = profileStore(useShallow((state) => state.profile));
  const companys = profile?.company;

  const lastValueYearMonth = `${moment().subtract(1, "months").format("YYYY-MM")}`;
  const currentDate = moment().startOf("month");
  const currentYear = moment().year();

  //   const [cntReRunJob, setCntReRunJob] = useState(0);
  const getSettingJob = settingJobStore(useShallow((state) => state.getSettingJob));
  const mergeData = settingJobStore(useShallow((state) => state.mergeData));
  const reRunJob = settingJobStore(useShallow((state) => state.reRunJob));
  const setLoadingJobs = settingJobStore(useShallow((state) => state.setLoadingJobs));
  const getJobById = settingJobStore(useShallow((state) => state.getJobById));

  const loadingEachJob = settingJobStore(useShallow((state) => state.loadingEachJob));
  const loading = settingJobStore(useShallow((state) => state.loading));
  const dataJobs = settingJobStore(useShallow((state) => state.data));

  const [dateToRun, setDateToRun] = useState<string>(lastValueYearMonth); // YYYY-MM
  const [yearFromRun, setYearFromRun] = useState<number>(currentYear - 1); // YYYY (use only duplicate job)
  const [yearToRun, setYearToRun] = useState<number>(currentYear); // YYYY (use only duplicate job)
  const [companyToRun, setCompanyToRun] = useState<string>("");

  // Please use the CirplasDev company when testing in the API Data Factory dev environment.
  const companyForTestDevEnv = "CirplasDev";
  const [companyName, setCompanyName] = useState<string>("");

  const [tmpRecordToRunTagPIJob, setTmpRecordToRunTagPIJob] = useState<ResponseSettingJob | null>(null);
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [visibleDupModal, setVisibleDupModal] = useState<boolean>(false);
  const [selectedJob, setSelectedJob] = useState<JobFromModule | null>(null);
  const [checkedMonths, setCheckedMonths] = useState<string[]>([]);
  const [relatedJob, setRelatedJob] = useState<string[] | null>([]);
  const [loadingDetailsJob, setLoadingDetailsJob] = useState<boolean>(false);
  const [loadingModal, setLoadingModal] = useState<boolean>(false);

  const [checkboxsOptionsMonths, setCheckboxsOptionsMonths] = useState<{ label: string; value: string; disabled: boolean }[]>([]);
  const [yearsOptionsFugitive, setYearsOptionsFugitive] = useState<{ label: string; value: string; disabled: boolean }[]>([]);

  useEffect(() => {
    getSettingJob();
  }, []);

  const pickFunction = (record: ResponseSettingJob) => {
    setSelectedJob(record.fromModule as JobFromModule);
    if (
      LISTS_RELATE_TAG_PI_JOB.includes(record.fromModule as JobFromModule) ||
      LISTS_RELATE_PRODUCTION_JOB.includes(record.fromModule as JobFromModule) ||
      LISTS_RELATE_FUGITIVE_JOB.includes(record.fromModule as JobFromModule)
    ) {
      setTmpRecordToRunTagPIJob(record);
      setVisibleModal(true);
      setRelatedJob([]);
    } else if (record.fromModule === JobFromModule.VOC_DUPLICATE_DATA) {
      setVisibleDupModal(true);
      setTmpRecordToRunTagPIJob(record);
      const related =
        dataJobs
          .filter((item) => [JobFromModule.COMBUSTION_CALCULATE, JobFromModule.FLARE_CALCULATE].includes(item.fromModule as JobFromModule))
          .map((job) => {
            return String(job._id);
          }) ?? [];
      setRelatedJob(related);
    } else {
      onReRunJob(record);
      setRelatedJob([]);
    }
  };

  const onReRunJob = async (record: ResponseSettingJob, query?: QueryTagPIJob) => {
    setLoadingDetailsJob(true);
    if (query?.companyId) {
      reRunJob(record._id, { ...query, taskName: record.taskName });
    } else {
      reRunJob(record._id, { ...query, taskName: record.taskName, companyName: companyName });
    }
    setLoadingJobs(record._id, true);
    const oldJobsData = dataJobs.find((item) => item._id === record._id) as ResponseSettingJob;
    mergeData({ ...oldJobsData, status: SettingJobStatusEnum.RUNNING });
    await new Promise((resolve) => setTimeout(resolve, 1000));
    getSettingJob();
    setLoadingDetailsJob(false);
  };

  useEffect(() => {
    // Poll payment status every 5 seconds
    const interval = setInterval(async () => {
      if (Object.values(loadingEachJob).includes(true)) {
        const keys = Object.keys(loadingEachJob);
        await Promise.all(
          keys.map(async (key) => {
            if (loadingEachJob[key] === true) {
              // fetch status
              getJobById(key);
              if (relatedJob && relatedJob?.length > 0) {
                relatedJob.forEach((relatedKey) => {
                  if (loadingEachJob[relatedKey] != true) getJobById(relatedKey);
                });
              }
            }
          }),
        );
      }
    }, 5000);

    return () => clearInterval(interval); // Clear interval on component unmount
  }, [loadingEachJob]);

  const onCloseModal = () => {
    setVisibleModal(false);
    setVisibleDupModal(false);
    setTmpRecordToRunTagPIJob(null);
    setDateToRun(lastValueYearMonth);
    setYearFromRun(currentYear - 1);
    setYearToRun(currentYear);
    setCompanyToRun("all");
    setCheckedMonths([]);
  };

  const yearsOptions = useMemo(() => {
    const years = generateYearArray();
    return years.map((year) => (
      <Select.Option key={year} value={year}>
        {year}
      </Select.Option>
    ));
  }, []);

  const companyOptions = useMemo(() => {
    if (companys) {
      setCompanyToRun("all");

      const filterCompany =
        selectedJob === JobFromModule.FUGITIVE_IMPORT_DATA ? companys.filter((item) => item.companyName != "TPE") : companys;

      return [
        <Select.Option key="all" value="all" label="All">
          All
        </Select.Option>,
        ...filterCompany.map((company) => (
          <Select.Option key={company._id} value={company._id} label={company.companyName}>
            {company.companyName}
          </Select.Option>
        )),
      ];
    }
  }, [companys, selectedJob]);

  const onChangeCompanyToRun = (value: string) => {
    setCompanyToRun(value);
    setCheckedMonths([]);

    if (!value) {
      setCompanyName(companyForTestDevEnv);
    } else {
      setCompanyName("");
    }
  };

  const onChangeDateToRun = (value: string, type: "year" | "month") => {
    const baseDate = moment(dateToRun, "YYYY-MM");
    const newDate = baseDate.clone();
    if (type === "year") {
      newDate.year(+value);
    }
    if (type === "month") {
      newDate.month(+value);
    }

    setCheckedMonths([]);
    setDateToRun(newDate.format("YYYY-MM"));
  };

  const onRunTagPiJob = () => {
    const isSummaryFugitiveJob = LISTS_RELATE_FUGITIVE_JOB.includes(selectedJob as JobFromModule);

    const yearPart = moment(dateToRun).year();
    const baseQuery: Partial<QueryTagPIJob> = {
      companyId: companyToRun,
    };

    const query: QueryTagPIJob = isSummaryFugitiveJob
      ? {
          ...baseQuery,
          year: yearPart,
          taskName: tmpRecordToRunTagPIJob?.taskName || "",
        }
      : {
          ...baseQuery,
          selectedMonths: checkedMonths.map((month) => `${yearPart}-${month}`),
        };

    onReRunJob(tmpRecordToRunTagPIJob as ResponseSettingJob, query);
    onCloseModal();
  };

  const onRunDuplicateJob = () => {
    if (yearFromRun && yearToRun) {
      const company = companyToRun != "all" ? companyToRun : "";
      const query: QueryDuplicateJob = {
        companyId: company,
        yearFrom: yearFromRun,
        yearTo: yearToRun,
      };
      onReRunJob(tmpRecordToRunTagPIJob as ResponseSettingJob, query);
      onCloseModal();
    }
  };

  const months_full = MONTHS_FULL_LISTS_PASCAL;
  const onChange = (checkedValues: string[]) => {
    setCheckedMonths(checkedValues);
  };

  const checkYearMonthApprove = async (job: JobFromModule, companyId: string, dateToRun: string) => {
    const yearRun = moment(dateToRun).year();
    let res: any = null;
    if (job === JobFromModule.FUGITIVE_IMPORT_TPE) {
      companyId = companys?.find((item) => item.companyName.toUpperCase() == "TPE")?._id || "";
    }

    switch (job) {
      case JobFromModule.COMBUSTION_CALCULATE:
        res = await getSubmitCombustion(companyId, yearRun);
        break;
      case JobFromModule.FLARE_CALCULATE:
        res = await getSubmitFlare(yearRun.toString(), companyId);
        break;
      case JobFromModule.FUGITIVE_RE_CALCULATE_VOCS_STATUS:
      case JobFromModule.FUGITIVE_RE_CALCULATE_SUMMARY_REPORT:
      case JobFromModule.FUGITIVE_IMPORT_TPE:
        res = await getSubmitFugitive({ companyId });
        break;
      default:
        console.warn(`No handler for job: ${job}`);
        return [];
    }

    const { code, data } = res?.data || {};
    // console.log("data", data);

    return code === 200 ? data : [];
  };

  // const checkboxsOptionsMonths = useMemo(() => {
  //   const activeMonths = selectedJob ? checkYearMonthApprove(selectedJob, companyToRun) : [];

  //   return months_full.map((month) => ({
  //     label: month,
  //     value: MonthFullNameMap[month as keyof typeof MonthFullNameMap],
  //     // disabled: activeMonths.find((item: any) =>  moment(item.value).format('MMMM').toLowerCase() === month.toLowerCase())?.statusFlowActive || false
  //   }));
  // }, [selectedJob, companyToRun]);

  useEffect(() => {
    const prepareOptions = async () => {
      setLoadingModal(true);
      const activeMonths = selectedJob ? await checkYearMonthApprove(selectedJob, companyToRun, dateToRun) : [];

      setLoadingModal(false);
      const options = months_full.map((month) => ({
        label: month,
        value: MonthFullNameMap[month as keyof typeof MonthFullNameMap],
        disabled:
          activeMonths.length > 0
            ? activeMonths.find((item: any) => item.label.toString().toLowerCase() === month.toString().toLowerCase())?.statusFlowActive ||
              moment(`${moment(dateToRun).year()}-${MonthFullNameMap[month as keyof typeof MonthFullNameMap]}`, "YYYY-MM").isAfter(
                currentDate,
              ) ||
              false
            : false,
      }));
      setCheckboxsOptionsMonths(options);
    };

    if (
      [JobFromModule.COMBUSTION_CALCULATE, JobFromModule.FLARE_CALCULATE, JobFromModule.PRODUCTION_IMPORT_DATA].includes(
        selectedJob as JobFromModule,
      )
    ) {
      prepareOptions();
    }
  }, [selectedJob, companyToRun, dateToRun]);

  useEffect(() => {
    const prepareOptionsYear = async () => {
      setLoadingModal(true);
      const approves = selectedJob ? await checkYearMonthApprove(selectedJob, companyToRun, dateToRun) : [];
      setLoadingModal(false);

      const years = generateYearArray().map((year) => ({
        label: year.toString(),
        value: year.toString(),
        disabled: approves.find((item: any) => item.year == year)?.statusFlowActive,
      }));

      setYearsOptionsFugitive(years);

      // console.log({ selectedJob, approves, years });
    };
    if (LISTS_RELATE_FUGITIVE_JOB.includes(selectedJob as JobFromModule)) prepareOptionsYear();
  }, [selectedJob, companyToRun, dateToRun]);

  const checkDisabledMonth = (fromModule: JobFromModule) => {
    if (LISTS_RELATE_FUGITIVE_JOB.includes(fromModule)) {
      return yearsOptionsFugitive.find((item) => item.value.toString() === moment(dateToRun).format("YYYY").toString())?.disabled;
    } else {
      return checkedMonths.length === 0;
    }
  };

  const shouldHideRow = (fromModule: JobFromModule) => {
    if ([JobFromModule.FUGITIVE_DELETE_SCGC, JobFromModule.FUGITIVE_CLEAN_DATA].includes(fromModule)) {
      // Hide
      return true;
    } else {
      return false;
    }
  };

  const genModalTitle = (fromModule: JobFromModule): string => {
    switch (fromModule) {
      case JobFromModule.COMBUSTION_CALCULATE:
      case JobFromModule.FLARE_CALCULATE:
        return "Please select the company, year and month to re-run.";
      case JobFromModule.PRODUCTION_IMPORT_DATA:
        return "Please select the year and month to re-run.";
      case JobFromModule.FUGITIVE_RE_CALCULATE_VOCS_STATUS:
      case JobFromModule.FUGITIVE_RE_CALCULATE_SUMMARY_REPORT:
      case JobFromModule.FUGITIVE_RE_MAPPING_AREA:
        return "Please select the company and year to re-run.";
      case JobFromModule.FUGITIVE_IMPORT_TPE:
        return "Please select the year to re-run.";
      case JobFromModule.FUGITIVE_IMPORT_DATA:
        return "Please select the company to re-run.";
      default:
        return "";
    }
  };

  return (
    <div>
      <Table<ResponseSettingJob>
        columns={getColumns(pickFunction)}
        // dataSource={dataJobs}
        dataSource={dataJobs.filter((item) => !shouldHideRow(item.fromModule as JobFromModule))}
        rowKey={(record) => `${record._id}`}
        loading={loading || loadingDetailsJob}
        pagination={false}
      />

      {/* Modal for job combustion, flare, production, fugitive */}
      <Modal
        title={<ModalTitle title={genModalTitle(selectedJob as JobFromModule) ?? ""} position={"center"} />}
        open={visibleModal}
        width={"50vw"}
        onCancel={onCloseModal}
        footer={null}
        maskClosable={false}
        centered
      >
        <Col>
          <br />
          {/* Company */}
          <Row className="justify-center items-center gap-x-6">
            {(LISTS_RELATE_TAG_PI_JOB.includes(selectedJob as JobFromModule) ||
              (LISTS_RELATE_FUGITIVE_JOB.includes(selectedJob as JobFromModule) && JobFromModule.FUGITIVE_IMPORT_TPE != selectedJob)) && (
              <div className="flex items-center gap-x-2">
                <Text>Company</Text>
                <Select className="w-[200px]" placeholder="Select Company" value={companyToRun} onChange={onChangeCompanyToRun}>
                  {companyOptions?.filter((option) =>
                    LISTS_RELATE_TAG_PI_JOB.includes(selectedJob as JobFromModule)
                      ? LISTS_TAGPI_COMPANY_JOB.includes(option.props.label.toString().toUpperCase()) || option.props.value === "all"
                      : true,
                  )}
                  {/* ,<Select.Option value="">CirplasDev</Select.Option> */}
                </Select>
              </div>
            )}

            {/* Year */}
            {LISTS_RELATE_FUGITIVE_JOB.includes(selectedJob as JobFromModule) ? (
              <div className="flex items-center gap-x-2">
                <Text>Year</Text>
                <Select
                  className="w-[200px]"
                  placeholder="Select year"
                  onChange={(value) => onChangeDateToRun(`${value}`, "year")}
                  value={moment(dateToRun).year()}
                  loading={loadingModal}
                >
                  {yearsOptionsFugitive.map((option) => (
                    <Select.Option key={option.value} value={option.value} disabled={option.disabled}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            ) : (
              <div className="flex items-center gap-x-2">
                <Text>Year</Text>
                <Select
                  className="w-[200px]"
                  placeholder="Select year"
                  onChange={(value) => onChangeDateToRun(`${value}`, "year")}
                  value={moment(dateToRun).year()}
                >
                  {yearsOptions}
                </Select>
              </div>
            )}
          </Row>

          <br />

          {[JobFromModule.COMBUSTION_CALCULATE, JobFromModule.FLARE_CALCULATE, JobFromModule.PRODUCTION_IMPORT_DATA].includes(
            selectedJob as JobFromModule,
          ) &&
            (loadingModal ? (
              <Spin className="flex justify-center items-center" />
            ) : (
              <Row className="w-full" justify={"center"}>
                <CheckboxGroup value={checkedMonths} onChange={onChange} style={{ width: "80%" }}>
                  <Row gutter={[24, 16]}>
                    {checkboxsOptionsMonths.map((month, index) => (
                      <Col span={8} key={index} style={{ textAlign: "left" }}>
                        <Checkbox value={month.value} disabled={month.disabled}>
                          {month.label}
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </CheckboxGroup>
              </Row>
            ))}

          <Row className="w-full" justify={"center"}>
            <Button
              className="mt-4"
              key="submit"
              type="primary"
              onClick={onRunTagPiJob}
              disabled={checkDisabledMonth(selectedJob as JobFromModule)}
            >
              Re-Run
            </Button>
          </Row>
        </Col>
      </Modal>

      {/* Modal for job duplicate data */}
      <Modal
        title={<ModalTitle title="Please select the company and year for duplicate data." position={"center"} />}
        open={visibleDupModal}
        width={"50vw"}
        onCancel={() => setVisibleDupModal(false)}
        footer={null}
        maskClosable={false}
        centered
      >
        <br />
        <Row className="justify-center items-center gap-x-6">
          <div className="flex items-center gap-x-2">
            <Text>Company</Text>
            <Select className="w-[200px]" placeholder="Select Company" value={companyToRun} onChange={onChangeCompanyToRun}>
              {companyOptions}
            </Select>
          </div>
        </Row>

        <br />
        <Row className="justify-center items-center gap-x-6">
          <div className="flex items-center gap-x-2">
            <Text>From</Text>
            <Select className="w-[200px]" placeholder="Select year" onChange={(value) => setYearFromRun(value)} value={yearFromRun}>
              {yearsOptions}
            </Select>

            <Text>To</Text>
            <Select className="w-[200px]" placeholder="Select year" onChange={(value) => setYearToRun(value)} value={yearToRun}>
              {yearsOptions}
            </Select>
          </div>
        </Row>

        <br />
        <Row className="w-full" justify={"center"}>
          <Button className="mt-4" key="submit" type="primary" onClick={onRunDuplicateJob}>
            Duplicate
          </Button>
        </Row>
      </Modal>
    </div>
  );
};

export default SettingJobTable;
